import { useEffect, useState } from "react";
import { Box, Typography, Skeleton, Tooltip } from "@mui/material";
import { fetchPartnerSalesByTerritory, fetchActiveRegularPartners, fetchCustomerBudgets } from "../../services/orderAnalytics.service";

import { getProRataProgress, percent, format2 } from "../../helpers/helper";

export default function SalesPartnerWise({
    selectedTerritory,
    dateParams,
    isDateRangeIncomplete,
}) {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { from, to, filterType, year } = dateParams || {};

    useEffect(() => {
        if (isDateRangeIncomplete || !selectedTerritory || !from || !to) {
            setRows([]);
            setError(null);
            setIsLoading(false);
            return;
        }

        let mounted = true;

        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const queryYear = year || new Date().getFullYear();
                const { elapsedDays, totalDays } = getProRataProgress(
                    filterType,
                    to,
                    queryYear
                );

                const [salesRes, partnersRes, budgetRes] = await Promise.all([
                    fetchPartnerSalesByTerritory({
                        territoryId: selectedTerritory,
                        from,
                        to,
                    }),
                    fetchActiveRegularPartners(),
                    fetchCustomerBudgets({
                        year: queryYear,
                        type: 'INTERNAL'
                    }),
                ]);

                if (!mounted) return;

                const partnersList = partnersRes?._embedded?.companies || (Array.isArray(partnersRes) ? partnersRes : []);

                const partnerLookup = {};
                const validPartnerIds = new Set();

                partnersList.forEach(p => {
                    if (!p.id) return;

                    if (String(p.territory) !== String(selectedTerritory)) return;

                    const country = p.countryName || p.defaultAddress?.country;
                    if (!country) return;

                    if (!p.shortKey) return;

                    let displayLocation = country;
                    if (country.toLowerCase() === 'india') {
                        const city = p.defaultAddress?.city;
                        if (city) {
                            displayLocation = city;
                        }
                    }
                    displayLocation = displayLocation.toUpperCase();

                    const partnerName = p.name || 'Unknown Partner';
                    const displayName = `${p.id} - ${partnerName} - ${displayLocation}`;

                    partnerLookup[p.id] = displayName;
                    validPartnerIds.add(p.id);
                });

                const budgetMap = {};
                (budgetRes || []).forEach(b => {
                    if (!b.customerNum) return;

                    if (validPartnerIds.has(b.customerNum)) {
                        budgetMap[b.customerNum] ??= { RET: 0, PRJ: 0 };
                        budgetMap[b.customerNum][b.categoryGroup] += Number(b.budget) || 0;
                    }
                });

                const salesMap = {};
                (salesRes || []).forEach(s => {
                    if (!s.customerNum) return;

                    if (validPartnerIds.has(s.customerNum)) {
                        salesMap[s.customerNum] ??= { RET: 0, PRJ: 0 };
                        salesMap[s.customerNum][s.category] += Number(s.total) || 0;
                    }
                });

                const rowData = Array.from(validPartnerIds).map(partnerId => {
                    const retailActual = salesMap[partnerId]?.RET ?? null;
                    const projectActual = salesMap[partnerId]?.PRJ ?? null;

                    const retailBudget = budgetMap[partnerId]?.RET ?? null;
                    const projectBudget = budgetMap[partnerId]?.PRJ ?? null;

                    const retailProRata = 
                    typeof retailBudget === "number"
                        ? (retailBudget * elapsedDays) / totalDays
                        : null;

                    const projectProRata = 
                    typeof projectBudget === "number"
                        ? (projectBudget * elapsedDays) / totalDays
                        : null;

                    const totalActual = 
                    (typeof retailActual === "number" || typeof projectActual === "number")
                        ? (retailActual ?? 0) + (projectActual ?? 0)
                        : null;

                    const totalBudget = (typeof retailBudget === "number" || typeof projectBudget === "number")
                        ? (retailBudget ?? 0) + (projectBudget ?? 0)
                        : null;

                    return {
                        id: partnerId,
                        name: partnerLookup[partnerId] || `${partnerId}`,

                        retail: format2(retailActual),
                        retailBudget: format2(retailBudget),
                        retailVsBudget: percent(retailActual ?? 0, retailBudget),
                        retailVsProRata: percent(retailActual ?? 0, retailProRata),

                        project: format2(projectActual),
                        projectBudget: format2(projectBudget),
                        projectVsBudget: percent(projectActual ?? 0, projectBudget),
                        projectVsProRata: percent(projectActual ?? 0, projectProRata),

                        total: format2(totalActual),
                        totalBudget: format2(totalBudget),
                    };
                });

                rowData.sort((a, b) => a.name.localeCompare(b.name));
                setRows(rowData);

            } catch (e) {
                console.error("Partner wise sales failed", e);
                if (mounted) {
                    setError("Unable to load partner wise sales data");
                    setRows([]);
                }
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        loadData();
        return () => { mounted = false; };
    }, [selectedTerritory, from, to, filterType, year, isDateRangeIncomplete]);

    const gridTemplate = "minmax(250px, 2fr) repeat(4, minmax(100px, 1fr)) repeat(4, minmax(100px, 1fr)) repeat(2, minmax(100px, 1fr))";

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
                minHeight: 200
            }}
        >
            <Typography sx={{ fontSize: 14, color: "secondary.main", mb: 2 }}>
                Sales – Partner wise
            </Typography>

            {isDateRangeIncomplete && (
                <Centered text="Select both From & To dates" color="warning.main" />
            )}

            {error && <Centered text={error} color="error.main" />}

            {isLoading && <SkeletonGrid template={gridTemplate} />}
            {!isLoading && !error && !isDateRangeIncomplete && rows.length === 0 && (
                <Box 
                sx={{ 
                    flex: 1, 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center", 
                    minHeight: 150 
                    }}
                    >
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                        No data available
                    </Typography>
                </Box>
            )}

            {!isLoading && !error && rows.length > 0 && (
                <Box sx={{ overflowX: "auto" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: gridTemplate, gap: 2, minWidth: 1400 }}>

                        <Box sx={{ gridColumn: "1 / span 1" }} />

                        <Box sx={{ gridColumn: "2 / span 4", borderBottom: 1, borderColor: "divider", pb: 1, mb: 1, textAlign: "center" }}>
                            <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 'bold', letterSpacing: 1 }}>
                                RETAIL
                            </Typography>
                        </Box>

                        <Box sx={{ gridColumn: "6 / span 4", borderBottom: 1, borderColor: "divider", pb: 1, mb: 1, textAlign: "center" }}>
                            <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 'bold', letterSpacing: 1 }}>
                                PROJECT
                            </Typography>
                        </Box>

                        <Box sx={{ gridColumn: "10 / span 2", borderBottom: 1, borderColor: "divider", pb: 1, mb: 1, textAlign: "center" }}>
                            <Typography sx={{ color: "secondary.main", fontSize: 13, fontWeight: 'bold', letterSpacing: 1 }}>
                                OVERALL
                            </Typography>
                        </Box>

                        <Header>PARTNER</Header>

                        <Header>RETAIL</Header>
                        <Header>BUDGET</Header>
                        <Header>ACTUAL VS BUDGET</Header>
                        <Header>ACTUAL VS PRO-RATA</Header>

                        <Header withDivider>PROJECT</Header>
                        <Header>BUDGET</Header>
                        <Header>ACTUAL VS BUDGET</Header>
                        <Header>ACTUAL VS PRO-RATA</Header>

                        <Header withDivider color="secondary.main">TOTAL SALES</Header>
                        <Header color="secondary.main">TOTAL BUDGET</Header>

                        {rows.map((row) => (
                            <Row key={row.id}>
                                <Tooltip title={row.name} placement="top-start" arrow>
                                    <Typography
                                        sx={{
                                            fontSize: 12, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden",
                                            textOverflow: "ellipsis", cursor: 'pointer', display: 'block'
                                        }}
                                    >
                                        {row.name}
                                    </Typography>
                                </Tooltip>

                                <Cell>{row.retail}</Cell>
                                <Cell>{row.retailBudget}</Cell>
                                <Cell>{row.retailVsBudget}</Cell>
                                <Cell>{row.retailVsProRata}</Cell>

                                <Cell withDivider>{row.project}</Cell>
                                <Cell>{row.projectBudget}</Cell>
                                <Cell>{row.projectVsBudget}</Cell>
                                <Cell>{row.projectVsProRata}</Cell>

                                <CellBold withDivider>{row.total}</CellBold>
                                <CellBold>{row.totalBudget}</CellBold>
                            </Row>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

const Header = ({ children, withDivider, color }) => (
    <Box sx={{
        borderLeft: withDivider ? 1 : 0,
        borderColor: "divider",
        pl: withDivider ? 2 : 0
    }}>
        <Typography sx={{ fontSize: 11, color: color || "text.secondary", whiteSpace: "nowrap", fontWeight: 600 }}>
            {children}
        </Typography>
    </Box>
);

const Cell = ({ children, withDivider }) => (
    <Box sx={{
        borderLeft: withDivider ? 1 : 0,
        borderColor: "divider",
        pl: withDivider ? 2 : 0
    }}>
        <Typography sx={{ fontSize: 12, color: "text.primary", whiteSpace: "nowrap" }}>
            {children ?? "--"}
        </Typography>
    </Box>
);

const CellBold = ({ children, withDivider }) => {
    return (
        <Box sx={{
            borderLeft: withDivider ? 1 : 0,
            borderColor: "divider",
            pl: withDivider ? 2 : 0
        }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "secondary.main" }}>
                {children ?? "--"}
            </Typography>
        </Box>
    );
};

const Row = ({ children }) => <>{children}</>;

const Centered = ({ text, color }) => (
    <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ color, fontSize: 12 }}>{text}</Typography>
    </Box>
);

const SkeletonGrid = ({ template }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: template, gap: 2, minWidth: 1400 }}>
        {Array.from({ length: 44 }).map((_, i) => (
            <Skeleton key={i} height={16} sx={{ bgcolor: "divider" }} />
        ))}
    </Box>
);